import React, { useEffect, useRef, useState } from "react";
import { useCloseDropdownEvents } from "../../components/useCloseDropdownEvents";
import useFetchSuggestions from "../../choices/useFetchSuggestions";
import { isEmpty } from "lodash";
import Suggestions, { suggestionsType } from "../../../admin/news-item/components/autosuggest/Suggestions";
import { eventInputType, eventKeyboardEventType } from "../../types/common";

interface AutoSuggestProps {
	currentInput: string;
	endpoint: string;
	getSuggestions?: (options: unknown, value: string) => unknown;
	id: string;
	name: string;
	onInputChange: (value: string) => void;
	onSuggestionSelect: (key: string, name: string) => void;
	placeholder?: string;
	searchByCode?: boolean;
}

export default function AutoSuggest({
	currentInput,
	endpoint,
	id,
	name,
	onInputChange,
	onSuggestionSelect,
	placeholder,
}: AutoSuggestProps) {
	const ref = useRef(null);
	const codeFetchParameter = "codes";
	const [dropdownIsOpened, setDropdownIsOpened] = useState(false);
	useCloseDropdownEvents(ref, dropdownIsOpened, () =>
		setDropdownIsOpened(!dropdownIsOpened),
	);
	const [suggestions, setSuggestions] = useState<suggestionsType>([]);
	const [fetchParams, setFetchParams] = useState({});
	const [cursor, setCursor] = useState(-1);
	const highlightedItem = suggestions[cursor];

	const shouldFetch = !isEmpty(fetchParams) && currentInput.length > 1;

	const { choices, loading, error } = useFetchSuggestions(
		endpoint,
		fetchParams,
		shouldFetch,
		500,
	);

	useEffect(() => {
		if (!loading && !error && choices && currentInput.length > 1) {
			setSuggestions(
				choices.map((item: { code: string; name: string }) => ({
					...item,
					disabled: currentInput === item.name,
				})),
			);
		}
	}, [loading, error, choices, currentInput]);

	const handleChange = (e: eventInputType) => {
		const value = e.target.value;

		if (value === "") {
			setSuggestions([]);
		}

		setFetchParams({
			[codeFetchParameter]: [],
			query: value,
		});
		setDropdownIsOpened(true);
		onInputChange(value);
	};

	const handleSuggestionSelected = (key: string, name: string) => {
		if (currentInput === name) {
			return;
		}

		onSuggestionSelect(key, name);
		setDropdownIsOpened(false);
		setCursor(-1);
		setSuggestions([]);
	};

	const handleKeyDown = (e: eventKeyboardEventType) => {
		if (e.key === "ArrowUp" && cursor > 0) {
			setCursor(cursor - 1);
		} else if (e.key === "ArrowDown" && cursor < suggestions.length - 1) {
			setCursor(cursor + 1);
		}

		if (e.key === "Enter") {
			setDropdownIsOpened(!dropdownIsOpened);

			if (highlightedItem) {
				const { code, name } = highlightedItem;
				handleSuggestionSelected(code, name);
			}
		}
	};

	return (
		<div ref={ref} className="criterion input-field autosuggest-container">
			<input
				type="text"
				className="form-control"
				id={id}
				name={name}
				value={currentInput}
				placeholder={placeholder}
				autoComplete="off"
				onFocus={() => setDropdownIsOpened(true)}
				onChange={handleChange}
				onKeyDown={handleKeyDown}
			/>

			<Suggestions
				value={currentInput}
				suggestions={suggestions}
				dropdownIsOpened={dropdownIsOpened}
				onSuggestionSelected={handleSuggestionSelected}
				active={highlightedItem?.code}
				loading={loading}
			/>
		</div>
	);
}
